import { addDays, weekday, type IsoDate } from './dates.ts';

// Working days in England and Wales, where the owner's banks settle. A direct debit or standing
// order due on a weekend or a bank holiday leaves the account on the next working day, so every
// "expected on" date is moved there before it is compared with the bank. The rules are those of
// gov.uk/bank-holidays; one-off holidays the rules cannot know are listed below.

/** Holidays declared by proclamation, and the regular ones they replaced. */
const ADDED: Record<number, readonly IsoDate[]> = {
  2011: ['2011-04-29'],
  2012: ['2012-06-04', '2012-06-05'],
  2020: ['2020-05-08'],
  2022: ['2022-06-02', '2022-06-03', '2022-09-19'],
  2023: ['2023-05-08'],
};
const MOVED: Record<number, readonly IsoDate[]> = {
  2012: ['2012-05-28'],
  2020: ['2020-05-04'],
  2022: ['2022-05-30'],
};

const pad = (n: number) => String(n).padStart(2, '0');

/** Easter Sunday (Gregorian calendar, anonymous algorithm). */
function easter(year: number): IsoDate {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** First Monday of a month. */
function firstMonday(year: number, month: number): IsoDate {
  const first = `${year}-${pad(month)}-01`;
  return addDays(first, (8 - weekday(first)) % 7);
}

/** Last Monday of a month. */
function lastMonday(year: number, month: number): IsoDate {
  const nextFirst = month === 12 ? `${year + 1}-01-01` : `${year}-${pad(month + 1)}-01`;
  const last = addDays(nextFirst, -1);
  return addDays(last, -((weekday(last) + 6) % 7));
}

const cache = new Map<number, ReadonlySet<IsoDate>>();

/** Bank holidays of England and Wales in a year. */
export function ukBankHolidays(year: number): ReadonlySet<IsoDate> {
  const known = cache.get(year);
  if (known) return known;
  const days = new Set<IsoDate>();
  // A fixed-date holiday on a weekend is taken on the next weekday that is not a holiday already
  // (Christmas on Saturday: Monday 27 and, for Boxing Day, Tuesday 28).
  const substitute = (date: IsoDate) => {
    let day = date;
    while (weekday(day) >= 6 || days.has(day)) day = addDays(day, 1);
    days.add(day);
  };
  substitute(`${year}-01-01`);
  const sunday = easter(year);
  days.add(addDays(sunday, -2));
  days.add(addDays(sunday, 1));
  days.add(firstMonday(year, 5));
  days.add(lastMonday(year, 5));
  days.add(lastMonday(year, 8));
  // Christmas first: both weekend days go to the following Monday and Tuesday, in order.
  const christmas = `${year}-12-25`;
  const boxing = `${year}-12-26`;
  if (weekday(christmas) < 6) days.add(christmas);
  if (weekday(boxing) < 6) days.add(boxing);
  if (weekday(christmas) >= 6) substitute(christmas);
  if (weekday(boxing) >= 6) substitute(boxing);
  for (const day of MOVED[year] ?? []) days.delete(day);
  for (const day of ADDED[year] ?? []) days.add(day);
  cache.set(year, days);
  return days;
}

export function isWorkingDay(date: IsoDate): boolean {
  return weekday(date) < 6 && !ukBankHolidays(Number(date.slice(0, 4))).has(date);
}

/** The date itself when banks work that day, otherwise the next day they do. */
export function nextWorkingDay(date: IsoDate): IsoDate {
  let day = date;
  while (!isWorkingDay(day)) day = addDays(day, 1);
  return day;
}

/** The date itself when banks work that day, otherwise the last day before it they do (payday). */
export function previousWorkingDay(date: IsoDate): IsoDate {
  let day = date;
  while (!isWorkingDay(day)) day = addDays(day, -1);
  return day;
}
